// Handwritten and PDF notes: the note's metadata says what it is, each page's strokes live in their own
// chunk so a page never pushes the note past the per-doc limit (docs/plans/notes.md D4).
//   notes/{id}                  { …note, format: 'ink' | 'pdf', paper, pages, pdfUrl?, pdfName?, pdfSizes? }
//   notes/{id}/chunks/ink-{n}   { page: n, strokes: '<JSON>', count, savedAt }     n = 0-based page
// Stroke JSON is written by the native ink view (modules/ops-ink, Ink.kt): page units 1000 wide.
// Pure — no I/O.

export const INK_FORMATS = ['ink', 'pdf'];
export const PAPERS = [
  { key: 'blank', label: 'Blank' },
  { key: 'lined', label: 'Lined' },
  { key: 'grid', label: 'Grid' },
  { key: 'dots', label: 'Dots' },
];
export const LETTER_ASPECT = 11 / 8.5;          // a paper page: US Letter, portrait
export const SKETCH_ASPECT = 0.62;              // a sketch dropped into a note: wide and short
export const INK_COLORS = ['#1A1A1A', '#1F5FD6', '#D93025', '#FB5446', '#188038', '#8E44AD'];
export const HIGHLIGHT_COLORS = ['#FFE234', '#7CFC9A', '#7FD8FF', '#FF9DE2'];
export const PEN_SIZES = [{ key: 'fine', w: 1.6 }, { key: 'medium', w: 2.6 }, { key: 'bold', w: 4.2 }];

export const isInkNote = (n) => !!n && INK_FORMATS.includes(n.format);
export const inkChunkId = (page) => `ink-${Math.max(0, Math.floor(Number(page) || 0))}`;
export const inkChunkPage = (id) => { const m = /^ink-(\d+)$/.exec(String(id || '')); return m ? Number(m[1]) : null; };

/** Metadata for a new handwritten or PDF note. */
export function newInkMeta({ format = 'ink', paper = 'lined', pdfUrl = null, pdfName = null, pdfSizes = null } = {}) {
  const pdf = format === 'pdf';
  const sizes = Array.isArray(pdfSizes) ? pdfSizes.map((s) => ({ w: Number(s.w) || 612, h: Number(s.h) || 792 })) : null;
  return {
    format: pdf ? 'pdf' : 'ink',
    paper: pdf ? 'blank' : (PAPERS.some((p) => p.key === paper) ? paper : 'lined'),
    pages: pdf ? Math.max(1, sizes?.length || 1) : 1,
    ...(pdf ? { pdfUrl, pdfName, pdfSizes: sizes } : {}),
    // list preview: there's no text to show, so say what it is
    text: pdf ? `PDF · ${pdfName || 'document'}` : 'Handwritten',
    words: 0,
  };
}

/** Page height ÷ width: a PDF page keeps its own shape, paper is Letter. */
export function pageAspect(note, page) {
  const s = note?.format === 'pdf' ? note?.pdfSizes?.[page] : null;
  return s && s.w > 0 ? s.h / s.w : LETTER_ASPECT;
}

/** Strokes in a page's JSON (0 for empty / unreadable). */
export function strokeCount(json) {
  if (!json) return 0;
  try { const o = typeof json === 'string' ? JSON.parse(json) : json; return Array.isArray(o?.strokes) ? o.strokes.length : 0; } catch { return 0; }
}

/** What the exporter draws: one entry per page, in order, blank pages included. */
export function inkExportPages(note, chunks = [], localPdf = null) {
  const byPage = new Map();
  for (const c of chunks) { const n = inkChunkPage(c.id); if (n != null) byPage.set(n, c.strokes || ''); }
  const out = [];
  for (let i = 0; i < Math.max(1, note?.pages || 1); i++) {
    out.push({
      strokes: byPage.get(i) || '', paper: note?.paper || 'blank', aspect: pageAspect(note, i),
      ...(note?.format === 'pdf' && localPdf ? { pdf: localPdf, pdfPage: i } : {}),
    });
  }
  return out;
}

/** A file-name-safe version of the note title for exports. */
export function exportName(title, fallback = 'note') {
  const s = String(title || '').trim().replace(/[^\w\- ]+/g, '').replace(/\s+/g, '-').slice(0, 60);
  return s || fallback;
}
